<template>
    <PlanbordGridRow class="planbord-grid-row-workload-requirements" :class="{showOnlyNegative: negativeOnly, empty: isEmpty(requirementsPerDay) && !hasOpenShifts, onlyPositive: hasOnlyPositiveValues, hasFunction: !!functionId}">
        <PlanbordGridLeftDataColumns>
            <PlanbordGridCell info centeredVertically>
                <div>
                    <span class="shiftCode">{{ shift.template!.code }}</span>
                    <template v-if="functionId && functionId in resourceFunctionNames">
                        <span>{{ resourceFunctionNames[functionId] }}</span>
                    </template>
                </div>
            </PlanbordGridCell>
            <PlanbordFunctionCell />
            <PlanbordCommentCell />
            <PlanbordStampStartDateCell />
        </PlanbordGridLeftDataColumns>
        <template v-for="day in roster.days" :key="day.date.getTime()">
            <PlanbordGridCell :class="getDayClass(day)">
                <LegacyWorkloadRequirementCell
                    v-if="requirementsPerDay[day.formattedForComputers]"
                    :requirement="requirementsPerDay[day.formattedForComputers]"
                    :shift="shift"
                    :date="day.date"
                />
                <OpenShiftIcon
                    v-for="openShift in getOpenShifts(day)"
                    :key="openShift.id"
                    :rosterId="roster.rosterId"
                    :date="day.date"
                    :openShift="openShift"
                />
            </PlanbordGridCell>
        </template>
    </PlanbordGridRow>
</template>

<script lang="ts">
    import LegacyWorkloadRequirementCell from '@/components/Planbord/WorkloadRequirements/LegacyWorkloadRequirementCell.vue';
    import OpenShiftIcon from '@/components/Planbord/WorkloadRequirements/OpenShiftIcon.vue';
    import {WorkloadRequirementAmount, RosterDay, WorkloadCount, RosterData} from '@/types/types';
    import {OpenShift, Shift} from '@cquential/api';
    import PlanbordGridLeftDataColumns from '@/components/Planbord/PlanbordGridLeftDataColumns.vue';
    import PlanbordGridRow from '@/components/Planbord/PlanbordGridRow.vue';
    import PlanbordGridCell from '@/components/Planbord/PlanbordGridCell.vue';
    import PlanbordFunctionCell from '@/components/Planbord/PlanbordFunctionCell.vue';
    import PlanbordCommentCell from '@/components/Planbord/PlanbordCommentCell.vue';
    import PlanbordStampStartDateCell from '@/components/Planbord/PlanbordStampStartDateCell.vue';
    import {ObjectUtils} from '@cquential/utils';
    import {Component, Vue, Prop} from 'vue-facing-decorator';

    @Component({
        components: {
            LegacyWorkloadRequirementCell,
            OpenShiftIcon,
            PlanbordGridLeftDataColumns,
            PlanbordGridRow,
            PlanbordGridCell,
            PlanbordFunctionCell,
            PlanbordCommentCell,
            PlanbordStampStartDateCell,
        },
    })
    export default class LegacyWorkloadRequirementsBodyRow extends Vue {
        @Prop({required: true}) public requirement!: Record<string, number>;
        @Prop() public functionId?: number;
        @Prop({required: true}) public shift!: Shift;
        @Prop({required: true}) public workloadCounts!: Record<number, Record<string, WorkloadCount>>;
        @Prop({required: true}) public roster!: RosterData;
        @Prop({required: true}) public resourceFunctionNames!: Record<number, string>;
        @Prop({type: Boolean}) public negativeOnly!: boolean;

        public calculateRequirement(date: string): WorkloadRequirementAmount {
            const required: number = this.requirement[date];
            // Define returnvalue and make it negative
            let shiftCount: number = 0;

            if (this.functionId === undefined) {
                // deployment exists on date, plus up the the returnvalue
                if (this.workloadCounts[this.shift.id] && this.workloadCounts[this.shift.id][date] && this.workloadCounts[this.shift.id][date].count) {
                    shiftCount += this.workloadCounts[this.shift.id][date].count;
                }
            } else {
                // deployment exists on date, plus up the the returnvalue
                if (this.workloadCounts[this.shift.id] && this.workloadCounts[this.shift.id][date] && this.workloadCounts[this.shift.id][date].functions && this.workloadCounts[this.shift.id][date].functions[this.functionId]) {
                    shiftCount += this.workloadCounts[this.shift.id][date].functions[this.functionId];
                }
            }

            // Return the returnValue as well as the class base on the returnvalue
            return {
                required,
                count: shiftCount,
            };
        }

        public getDayClass(day: RosterDay): string {
            let cssClass: string = day.holiday ? 'holiday ' : '';

            if (day.weekend) {
                cssClass = 'weekend ' + cssClass;
            }

            if (day.ended) {
                cssClass = 'ended ' + cssClass;
            }

            return cssClass + 'requirement';
        }

        public get hasOnlyPositiveValues(): boolean {
            const requirements: WorkloadRequirementAmount[] = Object.values(this.requirementsPerDay);

            return requirements.every((requirement: WorkloadRequirementAmount): boolean => requirement.required <= requirement.count);
        }

        public get requirementsPerDay(): Record<string, WorkloadRequirementAmount> {
            const result: Record<string, WorkloadRequirementAmount> = {};

            for (const day of this.roster.days) {
                if (typeof this.requirement[day.formattedForComputers] !== 'undefined') {
                    result[day.formattedForComputers] = this.calculateRequirement(day.formattedForComputers);
                }
            }

            for (const date in result) {
                if (result[date].required === result[date].count) {
                    delete result[date];
                }
            }

            return result;
        }

        public get hasOpenShifts(): boolean {
            return this.shift.id in this.roster.openShifts;
        }

        public getOpenShifts(day: RosterDay): OpenShift[] {
            if (this.functionId
                || !(this.shift.id in this.roster.openShifts)
                || !(day.formattedForComputers in this.roster.openShifts[this.shift.id])
            ) {
                return [];
            }

            return this.roster.openShifts[this.shift.id][day.formattedForComputers] ?? [];
        }

        public isEmpty(obj: Record<string, unknown>): boolean {
            // bullshit wrapper b/c we use a template
            return ObjectUtils.isEmpty(obj);
        }
    }
</script>

<style lang="scss" scoped>
    span {
        display: block;
        padding: 0.1rem 0.25rem;
        border-radius: 1rem;
    }

    .empty, .showOnlyNegative.onlyPositive {
        display: none;
    }

    .shiftCode {
        font-weight: 700;
    }

    // ALARM - CROSS COMPONENT - ALARM //
    .showOnlyNegative :deep(.requirement-fulfilled) {
        display: none;
    }

    .planbord-grid-row-workload-requirements:hover .shiftCode {
        background: var(--active-marker-background);
        color: var(--theme-text-color);
    }
</style>
